import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import tenantUsersService from '../services/tenantUsersService';
import Pagination from '../components/Pagination';

const PendingUsers = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null, action: null });
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  const { user } = useSelector((state) => state.auth);

  // Funkcija za dohvatanje korisnika koji čekaju na odobrenje
  const fetchPendingUsers = useCallback(async () => {
    try {
      setLoading(true);
      const tenantId = user?.tenantId?._id || user?.tenantId;
      
      if (!tenantId) {
        setPendingUsers([]);
        setTotalCount(0);
        return;
      }
      
      // Koristimo tenantUsersService.getTenantUsers sa approved: false filter
      const response = await tenantUsersService.getTenantUsers({ 
        tenantId, 
        page: page + 1, 
        limit: rowsPerPage,
        approved: false  // Filter za korisnika koji čekaju odobrenje
      });
      
      setPendingUsers(response.users || response || []);
      setTotalCount(response.total || 0);
    } catch (error) {
      console.error('Error fetching pending users:', error);
      showAlert('Greška prilikom dohvatanja korisnika koji čekaju na odobrenje', 'error');
      setPendingUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId, page, rowsPerPage]);

  useEffect(() => {
    if (user?.tenantId) {
      fetchPendingUsers();
    }
  }, [fetchPendingUsers, user?.tenantId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 5000);
  };

  const handleAction = async (userId, action) => {
    try {
      setActionLoading(userId);
      
      if (action === 'approve') {
        await tenantUsersService.approveUser(userId, user?.tenantId);
        showAlert('Korisnik je uspešno odobren', 'success');
      } else if (action === 'reject') {
        await tenantUsersService.rejectUser(userId, user?.tenantId);
        showAlert('Korisnik je odbačen', 'info');
      }
      
      // Refresh the list
      await fetchPendingUsers();
      setConfirmDialog({ open: false, user: null, action: null });
    } catch (error) {
      console.error(`Error ${action}ing user:`, error);
      showAlert(`Greška prilikom ${action === 'approve' ? 'odobravanja' : 'odbacivanja'} korisnika`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const openConfirmDialog = (user, action) => {
    setConfirmDialog({ open: true, user, action });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, user: null, action: null });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('sr-RS', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert({ show: false, message: '', severity: 'success' })}>
          {alert.message}
        </Alert>
      )}

      {pendingUsers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Nema korisnika koji čekaju na odobrenje registracije
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Korisnik</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Datum registracije</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Akcije</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingUsers.map((pendingUser) => (
                  <TableRow key={pendingUser._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="action" />
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {pendingUser.firstName} {pendingUser.lastName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {pendingUser.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon color="action" fontSize="small" />
                        {pendingUser.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {formatDate(pendingUser.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label="Čeka odobrenje" 
                        color="warning" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Odobri korisnika">
                          <IconButton
                            color="success"
                            onClick={() => openConfirmDialog(pendingUser, 'approve')}
                            disabled={actionLoading === pendingUser._id}
                          >
                            {actionLoading === pendingUser._id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CheckIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Odbaci korisnika">
                          <IconButton
                            color="error"
                            onClick={() => openConfirmDialog(pendingUser, 'reject')}
                            disabled={actionLoading === pendingUser._id}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Redova po stranici:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} od ${count !== -1 ? count : `više od ${to}`}`
            }
          />
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {confirmDialog.action === 'approve' ? 'Odobri korisnika' : 'Odbaci korisnika'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {confirmDialog.action === 'approve' 
              ? `Da li ste sigurni da želite da odobrite korisnika ${confirmDialog.user?.firstName} ${confirmDialog.user?.lastName}?`
              : `Da li ste sigurni da želite da odbacite korisnika ${confirmDialog.user?.firstName} ${confirmDialog.user?.lastName}?`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} color="primary">
            Otkaži
          </Button>
          <Button 
            onClick={() => handleAction(confirmDialog.user?._id, confirmDialog.action)} 
            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
            variant="contained"
          >
            {confirmDialog.action === 'approve' ? 'Odobri' : 'Odbaci'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingUsers;